export * from './UserService';
export * from './TransRoutesService';
export * from './DriverService';
export * from './VehicleService';
export * from './EmployeeService';
export * from './CustomerService';
export * from './CustomerAbsenseService';
export * from './AuthService';
export * from './RouteTemplateService';
export * from './ReportService';
export * from './DispatcherService';
export * from './MessageService';
export * from './CenterPhoneService';
export * from './ResourceService';
export * from './EventsService';
export * from './EventRequestService';
export * from './SignatureRequestService';
export * from './VehicleRepairService';
export * from './LabelService';
export * from './SeatingService';
export * from './AttendanceNoteService';
export * from './CarouselService';
export * from './DailyRoutesTemplateService';
export * from './TemplateFileService';
export * from './ActionLogService';
export * from './RouteApiErrorService';
export * from './WeatherService';
export * from './InfoScreenService';
export * from './UserPerfService';
export * from './FileService';
export * from './admissionRecords';
export * from './MedicalFormService';
export * from './ApkService';

// Utility functions
export const parseDateFromBackend = (dateString) => {
  if (!dateString) return null;
  
  // Handle MM/DD/YYYY format
  if (typeof dateString === 'string' && dateString.includes('/')) {
    const [month, day, year] = dateString.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  
  // Handle other date formats
  return new Date(dateString);
};

/** Full English month name from admission date (e.g. 1/3 → January). Read-only derived field. */
export const getAnniversaryMonthFromAdmissionDate = (dateInput) => {
  if (!dateInput) return '';
  const d = dateInput instanceof Date ? dateInput : parseDateFromBackend(dateInput);
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'long' });
};

/** MM/DD/YYYY for display */
export const formatDateUsMdY = (dateInput) => {
  const d = dateInput instanceof Date ? dateInput : parseDateFromBackend(dateInput);
  if (!d || Number.isNaN(d.getTime())) return '';
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
};

/** Whole calendar days between two local dates (not wall-clock ms; DST can add/subtract an hour). */
const calendarDaysBetween = (fromDate, toDate) => {
  const fromUtc = Date.UTC(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const toUtc = Date.UTC(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
  return Math.round((toUtc - fromUtc) / 86400000);
};

/** Next ADCAPS due = Adcaps Completed Date + 120 days */
export const computeNextAdcapsDueDate = (dateInput) => {
  const d = dateInput instanceof Date ? new Date(dateInput.getTime()) : parseDateFromBackend(dateInput);
  if (!d || Number.isNaN(d.getTime())) return null;
  const out = new Date(d);
  out.setDate(out.getDate() + 120);
  return out;
};

/** Whole days from local today to next ADCAPS due (can be negative if past due). */
export const computeAdcapsRemainingDays = (dateInput) => {
  const due = computeNextAdcapsDueDate(dateInput);
  if (!due) return '';
  return String(calendarDaysBetween(new Date(), due));
};

/** Whole days from local today to a deadline date (MM/DD/YYYY or Date); negative if past. Empty if unparseable. */
export const computeDaysFromTodayToDate = (dateInput) => {
  if (!dateInput) return '';
  const d = dateInput instanceof Date ? new Date(dateInput.getTime()) : parseDateFromBackend(dateInput);
  if (!d || Number.isNaN(d.getTime())) return '';
  return String(calendarDaysBetween(new Date(), d));
};

/**
 * "Last, First Middle (Chinese / Preferred Name)" for lists and view pages.
 * Falls back to `name` when name parts are missing; uses `name_cn` in parentheses when present.
 */
export const formatCustomerDisplayName = (customer) => {
  if (customer == null) return '';
  const last = `${customer.lastname ?? customer.lastName ?? ''}`.trim();
  const first = `${customer.firstname ?? customer.firstName ?? ''}`.trim();
  const middle = `${customer.middle_name ?? ''}`.trim();
  const cn = `${customer.name_cn ?? ''}`.trim();

  const firstMiddle = [first, middle].filter(Boolean).join(' ');
  let main = '';
  if (last) {
    main = firstMiddle ? `${last}, ${firstMiddle}` : last;
  } else if (firstMiddle) {
    main = firstMiddle;
  } else {
    main = `${customer.name ?? ''}`.trim();
  }

  if (!main) {
    return cn;
  }
  if (cn) {
    return `${main} (${cn})`;
  }
  return main;
};

/** A–Z / Z–A for dashboard customer name column: last, then first, then middle. */
export const compareCustomersByNameColumn = (a, b) => {
  const t = (x, y) => `${x ?? ''}`.toLowerCase().localeCompare(`${y ?? ''}`.toLowerCase(), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
  const byLast = t(a?.lastname, b?.lastname);
  if (byLast !== 0) return byLast;
  const byFirst = t(a?.firstname, b?.firstname);
  if (byFirst !== 0) return byFirst;
  return t(a?.middle_name, b?.middle_name);
};